import axios from 'axios';
import { getToken } from './auth';

// Definindo a URL base da API do backend
const API_URL = process.env.REACT_APP_API_URL || 'https://api.planmyplate.io';

// Configurando o axios para incluir automaticamente o token de autenticação
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Classe genérica para operações CRUD
export class ApiService<T> {
  endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  // Obter todos os registros
  async getAll(): Promise<T[]> {
    try {
      const response = await axios.get(`${API_URL}/${this.endpoint}`);
      return response.data;
    } catch (error: any) {
      this.handleError(error);
      return [];
    }
  }

  // Obter um registro pelo ID
  async getById(id: string): Promise<T | null> {
    try {
      const response = await axios.get(`${API_URL}/${this.endpoint}/${id}`);
      return response.data;
    } catch (error: any) {
      this.handleError(error);
      return null;
    }
  }

  // Criar um novo registro
  async create(data: Partial<T>): Promise<T | null> {
    try {
      const response = await axios.post(`${API_URL}/${this.endpoint}`, data);
      return response.data;
    } catch (error: any) {
      this.handleError(error);
      return null;
    }
  }

  // Atualizar um registro existente
  async update(id: string, data: Partial<T>): Promise<T | null> {
    try {
      const response = await axios.put(`${API_URL}/${this.endpoint}/${id}`, data);
      return response.data;
    } catch (error: any) {
      this.handleError(error);
      return null;
    }
  }

  // Excluir um registro
  async delete(id: string): Promise<boolean> {
    try {
      await axios.delete(`${API_URL}/${this.endpoint}/${id}`);
      return true;
    } catch (error: any) {
      this.handleError(error);
      return false;
    }
  }

  // Método para buscar dados com paginação
  async getPaginated(page: number = 1, perPage: number = 10, filters: Record<string, any> = {}): Promise<{data: T[], total: number, page: number, perPage: number}> {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        ...filters
      });
      
      const response = await axios.get(`${API_URL}/${this.endpoint}?${queryParams}`);
      
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.current_page || page,
        perPage: response.data.per_page || perPage
      };
    } catch (error: any) {
      this.handleError(error);
      return {
        data: [],
        total: 0,
        page,
        perPage
      };
    }
  }

  // Tratamento de erros comum
  protected handleError(error: any): void {
    console.error(`Erro no serviço ${this.endpoint}:`, error);
    
    if (error.response) {
      // Erro de resposta do servidor (4xx, 5xx)
      console.error('Resposta do servidor:', error.response.data);
      
      // Se o erro for 401 (não autorizado), podemos redirecionar para a página de login
      if (error.response.status === 401) {
        // Limpar token e redirecionar
        localStorage.removeItem('admin_token');
        window.location.href = '/login';
      }
    } else if (error.request) {
      // Erro de conexão (sem resposta)
      console.error('Sem resposta do servidor:', error.request);
    } else {
      // Erro na configuração da requisição
      console.error('Erro na configuração da requisição:', error.message);
    }
    
    throw error;
  }
}

// Serviços específicos para cada entidade do sistema
export const UserService = new ApiService<any>('admin/users');
export const RecipeService = new ApiService<any>('admin/recipes');
export const MealPlanService = new ApiService<any>('admin/meal-plans');
export const CategoryService = new ApiService<any>('admin/categories');
export const SettingsService = new ApiService<any>('admin/settings');

// Função para obter estatísticas do dashboard
export async function getDashboardStats(): Promise<any> {
  try {
    // Corrigindo para garantir que o prefixo /api esteja incluído na URL
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.planmyplate.io';
    const response = await axios.get(`${baseUrl}/api/admin/dashboard/stats`);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao obter estatísticas do dashboard:', error);
    return null;
  }
}

const databaseServices = {
  UserService,
  RecipeService,
  MealPlanService,
  CategoryService,
  SettingsService,
  getDashboardStats
};

export default databaseServices;